import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Key as KeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { fetchUsers, updateUser, deleteUser, createUser, changeUserPassword } from '../store/slices/usersSlice';
import PageHeader from '../components/PageHeader';
import api from '../services/api';
import { formatApiError } from '../utils/errorMessage';

function UserAdmin() {
  const dispatch = useDispatch();
  const { users, error } = useSelector((state) => state.users);
  const { userInfo } = useSelector((state) => state.auth);
  const { activeCompanyId, list: myCompanies } = useSelector((state) => state.company);
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  const isCompanyAdmin = activeCompany?.role === 'admin' || userInfo?.is_superadmin;
  
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [createDialog, setCreateDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [editFormData, setEditFormData] = useState({
    is_active: false,
    is_approved: false,
    role: 'user',
    full_name: '',
    email: '',
  });
  
  const [createFormData, setCreateFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'user',
    is_active: true,
    is_approved: true,
  });
  
  const [newPassword, setNewPassword] = useState('');

  // Invitation flow state
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'user' });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteFeedback, setInviteFeedback] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);

  const refreshInvitations = async () => {
    try {
      const { data } = await api.get('/api/users/company/invitations');
      setPendingInvites(data || []);
    } catch (_) {
      // Non-fatal — list just stays empty.
    }
  };

  useEffect(() => {
    dispatch(fetchUsers());
    refreshInvitations();
  }, [dispatch]);

  const sendInvite = async () => {
    setInviteSubmitting(true);
    setInviteFeedback(null);
    try {
      await api.post('/api/users/company/invite', inviteForm);
      setInviteFeedback({ severity: 'success', message: `Invitation sent to ${inviteForm.email}.` });
      setInviteForm({ email: '', role: 'user' });
      refreshInvitations();
    } catch (e) {
      setInviteFeedback({ severity: 'error', message: formatApiError(e, 'Failed to send invitation.') });
    } finally {
      setInviteSubmitting(false);
    }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/api/users/company/invitations/${id}`);
      refreshInvitations();
    } catch (_) { /* ignore */ }
  };

  // Inline status for the resend action, keyed by invite id so a long
  // operator can resend several in a row and see each result land.
  const [resendStatus, setResendStatus] = useState({});  // {[id]: {kind, message}}
  const [resending, setResending] = useState({});         // {[id]: bool}

  const resendInvite = async (id, email) => {
    setResending((s) => ({ ...s, [id]: true }));
    setResendStatus((s) => ({ ...s, [id]: null }));
    try {
      const { data } = await api.post(`/api/users/company/invitations/${id}/resend`);
      if (data?.email_sent) {
        setResendStatus((s) => ({
          ...s,
          [id]: { kind: 'ok', message: `Resent to ${email}.` },
        }));
      } else {
        setResendStatus((s) => ({
          ...s,
          [id]: {
            kind: 'error',
            message: data?.email_error || 'Resend failed — check Operator → Email config.',
          },
        }));
      }
      refreshInvitations();
    } catch (e) {
      setResendStatus((s) => ({
        ...s,
        [id]: { kind: 'error', message: formatApiError(e, 'Resend failed.') },
      }));
    } finally {
      setResending((s) => ({ ...s, [id]: false }));
    }
  };

  const handleOpenEditDialog = (user) => {
    setEditFormData({
      is_active: user.is_active,
      is_approved: user.is_approved,
      role: user.role,
      full_name: user.full_name || '',
      email: user.email || '',
    });
    setEditDialog({ open: true, user });
  };

  const handleCloseEditDialog = () => {
    setEditDialog({ open: false, user: null });
  };

  const handleUpdateUser = async () => {
    const updates = {};
    
    if (editFormData.is_active !== editDialog.user.is_active) {
      updates.is_active = editFormData.is_active;
    }
    if (editFormData.is_approved !== editDialog.user.is_approved) {
      updates.is_approved = editFormData.is_approved;
    }
    if (editFormData.role !== editDialog.user.role) {
      updates.role = editFormData.role;
    }
    if (editFormData.full_name !== editDialog.user.full_name) {
      updates.full_name = editFormData.full_name || null;
    }
    if (editFormData.email !== editDialog.user.email) {
      updates.email = editFormData.email;
    }
    
    if (Object.keys(updates).length > 0) {
      await dispatch(updateUser({ userId: editDialog.user.id, updates })).unwrap();
    }
    
    handleCloseEditDialog();
  };

  const handleQuickToggle = async (user, field) => {
    const updates = { [field]: !user[field] };
    await dispatch(updateUser({ userId: user.id, updates })).unwrap();
  };

  const handleDeleteUser = async () => {
    if (deleteDialog.user) {
      try {
        await dispatch(deleteUser(deleteDialog.user.id)).unwrap();
        setDeleteDialog({ open: false, user: null });
      } catch (_err) {
        // Error already surfaced via Redux state.error.
        setDeleteDialog({ open: false, user: null });
      }
    }
  };

  const handleCreateUser = async () => {
    try {
      await dispatch(createUser(createFormData)).unwrap();
      setCreateDialog(false);
      setCreateFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
        role: 'user',
        is_active: true,
        is_approved: true,
      });
    } catch (_err) {
      // Error already surfaced via Redux state.error.
    }
  };

  const handleChangePassword = async () => {
    if (passwordDialog.user && newPassword) {
      try {
        await dispatch(changeUserPassword({ 
          userId: passwordDialog.user.id, 
          newPassword 
        })).unwrap();
        setPasswordDialog({ open: false, user: null });
        setNewPassword('');
      } catch (_err) {
        // Error already surfaced via Redux state.error.
      }
    }
  };

  const filteredUsers = users.filter(user => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!user.username.toLowerCase().includes(search) &&
          !user.email.toLowerCase().includes(search) &&
          !(user.full_name && user.full_name.toLowerCase().includes(search))) {
        return false;
      }
    }
    
    // Role filter
    if (roleFilter !== 'all' && user.role !== roleFilter) {
      return false;
    }
    
    // Status filter
    if (statusFilter === 'active' && !user.is_active) return false;
    if (statusFilter === 'inactive' && user.is_active) return false;
    if (statusFilter === 'pending' && user.is_approved) return false;
    if (statusFilter === 'approved' && !user.is_approved) return false;
    
    return true;
  });

  const getStatusChip = (user) => {
    if (!user.is_approved) {
      return <Chip label="Pending Approval" color="warning" size="small" />;
    } else if (!user.is_active) {
      return <Chip label="Inactive" color="default" size="small" />;
    } else {
      return <Chip label="Active" color="success" size="small" />;
    }
  };

  const getRoleChip = (role) => {
    // Distinguish company-admin from site-admin in the label so it's
    // clear this only confers admin within the active company. Site
    // admin (is_superadmin) is managed in the operator panel.
    const labelByRole = {
      admin: 'COMPANY ADMIN',
      user: 'USER',
      venue_manager: 'VENUE MANAGER',
    };
    return (
      <Chip
        label={labelByRole[role] || (role || '').toUpperCase()}
        color={role === 'admin' ? 'secondary' : 'primary'}
        size="small"
        variant={role === 'admin' ? 'filled' : 'outlined'}
      />
    );
  };

  // Only show this page to company admins (or superadmins)
  if (!isCompanyAdmin) {
    return (
      <Box>
        <Alert severity="error">
          You don't have permission to manage users in this workspace.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle={activeCompany ? `Members of ${activeCompany.name}` : 'Workspace members'}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setInviteDialog(true)} sx={{ whiteSpace: 'nowrap' }}>
              Invite Member
            </Button>
            <Button variant="outlined" onClick={() => setCreateDialog(true)} sx={{ whiteSpace: 'nowrap' }}>
              Add Directly (legacy)
            </Button>
          </Box>
        }
      />

      {/* Pending invitations — only shown when there are any. */}
      {pendingInvites.length > 0 && (
        <Paper sx={{ mb: 2, p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Pending invitations</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingInvites.map((inv) => {
                  const r = resendStatus[inv.id];
                  return (
                    <React.Fragment key={inv.id}>
                      <TableRow>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell>{inv.role}</TableCell>
                        <TableCell>{inv.expires_at ? new Date(inv.expires_at).toLocaleString() : '—'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="small"
                            onClick={() => resendInvite(inv.id, inv.email)}
                            disabled={!!resending[inv.id]}
                            sx={{ mr: 1 }}
                          >
                            {resending[inv.id] ? 'Resending…' : 'Resend'}
                          </Button>
                          <Button size="small" color="error" onClick={() => revokeInvite(inv.id)}>Revoke</Button>
                        </TableCell>
                      </TableRow>
                      {r && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ pt: 0, pb: 1, borderBottom: 'none' }}>
                            <Alert
                              severity={r.kind === 'ok' ? 'success' : 'error'}
                              sx={{ whiteSpace: 'pre-line' }}
                              onClose={() => setResendStatus((s) => ({ ...s, [inv.id]: null }))}
                            >
                              {r.message}
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteDialog} onClose={() => { setInviteDialog(false); setInviteFeedback(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Invite a member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'll email a tokenized link to this address. The recipient signs in
            (or creates an account with that email) and accepts. No membership
            is created until they accept — typos and guessed addresses can't
            quietly grant access.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
            autoFocus
          />
          <FormControl fullWidth>
            <InputLabel id="invite-role">Role</InputLabel>
            <Select
              labelId="invite-role"
              label="Role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Company Admin</MenuItem>
              <MenuItem value="venue_manager">Venue Manager</MenuItem>
            </Select>
          </FormControl>
          {inviteFeedback && (
            <Alert severity={inviteFeedback.severity} sx={{ mt: 2 }}>{inviteFeedback.message}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInviteDialog(false); setInviteFeedback(null); }}>Close</Button>
          <Button variant="contained" onClick={sendInvite} disabled={!inviteForm.email || inviteSubmitting}>
            {inviteSubmitting ? 'Sending…' : 'Send invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            id="user-search"
            name="userSearch"
            label="Search users"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="role-filter-label" htmlFor="role-filter-select">Role</InputLabel>
            <Select
              id="role-filter-select"
              name="roleFilter"
              labelId="role-filter-label"
              value={roleFilter}
              label="Role"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="admin">Company Admin</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="venue_manager">Venue Manager</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="status-filter-label" htmlFor="status-filter-select">Status</InputLabel>
            <Select
              id="status-filter-select"
              name="statusFilter"
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Full Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {user.username}
                  </Typography>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.full_name || '-'}</TableCell>
                <TableCell>{getRoleChip(user.role)}</TableCell>
                <TableCell>{getStatusChip(user)}</TableCell>
                <TableCell>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell align="right">
                  {!user.is_approved && (
                    <Tooltip title="Approve User">
                      <IconButton size="small" color="success" onClick={() => handleQuickToggle(user, 'is_approved')}>
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {user.is_active ? (
                    <Tooltip title="Deactivate User">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleQuickToggle(user, 'is_active')}
                          disabled={user.id === userInfo.id}
                        >
                          <BlockIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Activate User">
                      <IconButton size="small" color="success" onClick={() => handleQuickToggle(user, 'is_active')}>
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Change Password">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setPasswordDialog({ open: true, user });
                        setNewPassword('');
                      }}
                    >
                      <KeyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit User">
                    <IconButton size="small" onClick={() => handleOpenEditDialog(user)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from workspace">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, user })}
                        disabled={user.id === userInfo.id}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit User Dialog */}
      <Dialog open={editDialog.open} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit User: {editDialog.user?.username}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              id="edit-user-full-name"
              name="full_name"
              fullWidth
              label="Full Name"
              value={editFormData.full_name}
              onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
              helperText="This name appears as the sender in email notifications"
            />
            
            <TextField
              id="edit-user-email"
              name="email"
              fullWidth
              label="Email"
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              required
            />
            
            <FormControlLabel
              control={
                <Switch
                  id="edit-user-active"
                  name="editUserActive"
                  checked={editFormData.is_active}
                  onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                  disabled={editDialog.user?.id === userInfo.id}
                />
              }
              label="Active"
            />
            
            <FormControlLabel
              control={
                <Switch
                  id="edit-user-approved"
                  name="editUserApproved"
                  checked={editFormData.is_approved}
                  onChange={(e) => setEditFormData({ ...editFormData, is_approved: e.target.checked })}
                />
              }
              label="Approved"
            />
            
            <FormControl fullWidth>
              <InputLabel id="edit-user-role-label" htmlFor="edit-user-role-select">Role</InputLabel>
              <Select
                id="edit-user-role-select"
                name="editUserRole"
                labelId="edit-user-role-label"
                value={editFormData.role}
                label="Role"
                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                disabled={editDialog.user?.id === userInfo.id}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Company Admin</MenuItem>
                <MenuItem value="venue_manager">Venue Manager</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the user "{deleteDialog.user?.username}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              id="create-user-username"
              name="username"
              label="Username"
              value={createFormData.username}
              onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })}
              fullWidth
              required
              helperText="At least 3 characters, alphanumeric"
              FormHelperTextProps={{
                sx: { textAlign: 'center' }
              }}
            />
            
            <TextField
              id="create-user-email"
              name="email"
              label="Email"
              type="email"
              value={createFormData.email}
              onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              id="create-user-password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={createFormData.password}
              onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
              fullWidth
              required
              helperText="At least 6 characters"
              FormHelperTextProps={{
                sx: { textAlign: 'center' }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              id="create-user-full-name"
              name="full_name"
              label="Full Name"
              value={createFormData.full_name}
              onChange={(e) => setCreateFormData({ ...createFormData, full_name: e.target.value })}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel id="create-user-role-label" htmlFor="create-user-role-select">Role</InputLabel>
              <Select
                id="create-user-role-select"
                name="createUserRole"
                labelId="create-user-role-label"
                value={createFormData.role}
                label="Role"
                onChange={(e) => setCreateFormData({ ...createFormData, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Company Admin</MenuItem>
                <MenuItem value="venue_manager">Venue Manager</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  id="create-user-active"
                  name="createUserActive"
                  checked={createFormData.is_active}
                  onChange={(e) => setCreateFormData({ ...createFormData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            
            <FormControlLabel
              control={
                <Switch
                  id="create-user-approved"
                  name="createUserApproved"
                  checked={createFormData.is_approved}
                  onChange={(e) => setCreateFormData({ ...createFormData, is_approved: e.target.checked })}
                />
              }
              label="Approved"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateUser} 
            variant="contained"
            disabled={!createFormData.username || !createFormData.email || !createFormData.password}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog.open} onClose={() => setPasswordDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change Password for {passwordDialog.user?.username}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              id="change-password-new"
              name="new_password"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              helperText="At least 6 characters"
              FormHelperTextProps={{
                sx: { textAlign: 'center' }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained"
            disabled={!newPassword || newPassword.length < 6}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserAdmin;